import React, { useEffect, useState } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import axios from "axios";
import { Crown, Radio, Users, Loader2 } from "lucide-react";
import { BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";

// Web landing for https://royalchat.online/join/{code}. On phones with the native app the OS
// opens the app directly (universal/app link); here we validate the code, preview the channel
// and send signed-in users straight to /app?join=CODE (which performs the join).
export default function JoinChannel() {
  const { code } = useParams();
  const { user, loading } = useAuth();
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/channels/invite/${encodeURIComponent(code)}`)
      .then((r) => setData(r.data)).catch(() => setErr(true));
    try { sessionStorage.setItem("rc_after_auth", `/app?join=${encodeURIComponent(code)}`); } catch {}
  }, [code]);

  if (loading || user === null) {
    return <div className="min-h-screen grid place-items-center bg-[var(--rc-bg)]"><Loader2 className="animate-spin text-[var(--rc-gold)]" /></div>;
  }
  if (user && !err) return <Navigate to={`/app?join=${encodeURIComponent(code)}`} replace />;

  return (
    <div className="min-h-screen bg-[var(--rc-bg)] text-[var(--rc-text)] flex flex-col">
      <header className="flex items-center gap-2 px-5 py-4 border-b border-[var(--rc-border)]">
        <Crown className="text-[var(--rc-gold)]" size={22} />
        <span className="font-heading text-lg">RoyalChat Nova</span>
      </header>

      <main className="flex-1 w-full max-w-md mx-auto px-5 py-8" data-testid="join-channel-page">
        {err ? (
          <div className="text-center py-16" data-testid="join-channel-error">
            <Radio size={36} className="mx-auto text-[var(--rc-muted)] mb-3" />
            <p className="text-[var(--rc-muted)]">{t("join_invalid_link")}</p>
            <Link to={user ? "/app" : "/"} className="inline-block mt-6 text-[var(--rc-gold)] underline">{t("join_back_home")}</Link>
          </div>
        ) : !data ? (
          <div className="grid place-items-center py-16"><Loader2 className="animate-spin text-[var(--rc-gold)]" /></div>
        ) : (
          <>
            <div className="rounded-3xl border border-[var(--rc-border)] bg-[var(--rc-surface)] p-6 text-center" data-testid="join-channel-card">
              <div className="h-20 w-20 mx-auto rounded-full bg-[var(--rc-bg)] overflow-hidden grid place-items-center text-[var(--rc-gold)] text-2xl font-semibold">
                {data.avatar ? <img src={data.avatar} alt="" className="h-full w-full object-cover" /> : (data.name || "?")[0]}
              </div>
              <h1 className="text-xl font-heading mt-4" data-testid="join-channel-name">{data.name}</h1>
              <div className="text-xs text-[var(--rc-muted)] mt-1 flex items-center justify-center gap-1">
                <Users size={13} /> {data.subscribers} {t("join_subscribers")}
              </div>
              {data.description && <p className="text-sm text-[var(--rc-muted)] mt-3 line-clamp-4">{data.description}</p>}
            </div>

            <div className="mt-6 rounded-2xl border border-[var(--rc-gold)]/30 bg-[var(--rc-gold)]/10 p-5 text-center">
              <p className="text-sm mb-3">{t("join_cta")}</p>
              <Link to="/register" data-testid="join-channel-register-btn" className="inline-block rounded-full bg-[var(--rc-gold)] text-black font-semibold px-8 py-3">{t("share_join_btn")}</Link>
              <div className="mt-3 text-xs text-[var(--rc-muted)]">{t("already_account")} <Link to="/login" data-testid="join-channel-login-link" className="text-[var(--rc-gold)] underline">{t("login")}</Link></div>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
